import * as React from "react";

import { Link } from "@remix-run/react";

import { cn } from "~/lib/utils";

// Base types
interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
    children?: React.ReactNode;
    className?: string;
}

type LinkProps = Omit<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    "href" | "children"
> &
    TypographyProps & {
        href: string;
    };

export const H1 = React.forwardRef<HTMLHeadingElement, TypographyProps>(
    ({ children, className, ...props }, ref) => (
        <h1
            ref={ref}
            className={cn(
                "font-display text-3xl leading-[2rem] font-bold text-rose-900 mt-0 mb-[0.6667rem] pt-0",
                className,
            )}
            {...props}
        >
            {children}
        </h1>
    ),
);
H1.displayName = "H1";

export const H2 = React.forwardRef<HTMLHeadingElement, TypographyProps>(
    ({ children, className, ...props }, ref) => (
        <h2
            ref={ref}
            className={cn(
                "font-display text-2xl leading-8 font-semibold text-brand-primary mt-0 mb-5",
                className,
            )}
            {...props}
        >
            {children}
        </h2>
    ),
);
H2.displayName = "H2";

export const H3 = React.forwardRef<HTMLHeadingElement, TypographyProps>(
    ({ children, className, ...props }, ref) => (
        <h3
            ref={ref}
            className={cn(
                "font-display text-xl leading-7 font-medium text-brand-primary mt-0 mb-4",
                className,
            )}
            {...props}
        >
            {children}
        </h3>
    ),
);
H3.displayName = "H3";

export const H4 = React.forwardRef<HTMLHeadingElement, TypographyProps>(
    ({ children, className, ...props }, ref) => (
        <h4
            ref={ref}
            className={cn(
                "font-body text-xl leading-7 font-semibold text-brand-primary mt-0 mb-3",
                className,
            )}
            {...props}
        >
            {children}
        </h4>
    ),
);
H4.displayName = "H4";

export const H5 = React.forwardRef<HTMLHeadingElement, TypographyProps>(
    ({ children, className, ...props }, ref) => (
        <h5
            ref={ref}
            className={cn(
                "font-body text-lg leading-6 font-medium text-gray-900 mt-0 mb-2",
                className,
            )}
            {...props}
        >
            {children}
        </h5>
    ),
);
H5.displayName = "H5";

export const P = React.forwardRef<HTMLParagraphElement, TypographyProps>(
    ({ children, className, ...props }, ref) => (
        <p
            ref={ref}
            className={cn(
                "font-body text-base leading-normal text-gray-800 mt-0 mb-6",
                className,
            )}
            {...props}
        >
            {children}
        </p>
    ),
);
P.displayName = "P";

export const A = React.forwardRef<
    HTMLAnchorElement,
    LinkProps & { href: string }
>(({ children, className, href, ...props }, ref) => (
    <Link
        ref={ref}
        className={cn(
            "text-secondary-foreground no-underline underline-offset-4 hover:text-brand-primary transition-colors duration-200",
            className,
        )}
        to={href}
        {...props}
    >
        {children}
    </Link>
));
A.displayName = "A";

export const Code = React.forwardRef<HTMLElement, TypographyProps>(
    ({ children, className, ...props }, ref) => (
        <code
            ref={ref}
            className={cn(
                "bg-rose-50 rounded-md px-2 py-1 whitespace-pre-wrap p-2 leading-loose",
                className,
            )}
            {...props}
        >
            {children}
        </code>
    ),
);
Code.displayName = "Code";

export const Label = React.forwardRef<HTMLLabelElement, TypographyProps>(
    ({ children, className, ...props }, ref) => (
        <label
            ref={ref}
            className={cn(
                "block text-sm font-body font-normal text-gray-700",
                className,
            )}
            {...props}
        >
            {children}
        </label>
    ),
);
Label.displayName = "Label";

export const UL = React.forwardRef<HTMLUListElement, TypographyProps>(
    ({ children, className, ...props }, ref) => (
        <ul ref={ref} className={cn("pl-5 mt-5 mb-5", className)} {...props}>
            {children}
        </ul>
    ),
);
UL.displayName = "UL";

export const OL = React.forwardRef<HTMLOListElement, TypographyProps>(
    ({ children, className, ...props }, ref) => (
        <ol ref={ref} className={cn("pl-5 mt-5 mb-5", className)} {...props}>
            {children}
        </ol>
    ),
);
OL.displayName = "OL";

export const LI = React.forwardRef<HTMLLIElement, TypographyProps>(
    ({ children, className, ...props }, ref) => (
        <li
            ref={ref}
            className={cn(
                "mt-2 mb-2 font-body text-base text-gray-800 leading-normal text-left list-disc",
                className,
            )}
            {...props}
        >
            {children}
        </li>
    ),
);
LI.displayName = "LI";

interface ListProps extends TypographyProps {
    ordered?: boolean;
}
